import { useState } from 'react';
import capitalize from 'lodash/capitalize';
import { arrayOf, number, shape, string } from 'prop-types';
import { track } from '@fff-web/fff-analytics';
import { getInvoiceLineItemsByUuid } from '../services/api/account';
import { formatCurrency } from '../utils/currency';
import InvoiceDetails from './InvoiceDetails';
import * as styles from './InvoiceHistory.module.css';

const FAILED_INVOICE_STATE = 'FAILED';

const getCalculatedValues = (totalInCents, creditAppliedInCents, hideCredit) => {
  if (hideCredit) {
    // adds the credit applied to the total because BE substracted it
    return {
      calculatedCreditApplied: 0,
      calculatedTotal: (totalInCents + creditAppliedInCents) / 100,
    };
  }
  return {
    calculatedCreditApplied: creditAppliedInCents,
    calculatedTotal: totalInCents / 100,
  };
};

const getTotalUnits = (lineItemDetail) =>
  lineItemDetail.reduce((result, item) => result + item.quantity, 0);

const getInvoiceData = async (uuid) => {
  return await getInvoiceLineItemsByUuid(uuid);
};

const formatDate = (dateToFormat) => {
  const date = new Date(dateToFormat);
  const formattedDate = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  return formattedDate;
};

const Invoice = ({ tabIndex, invoice }) => {
  const [invoiceData, setInvoiceData] = useState(null);
  const {
    uuid,
    createdAt,
    creditAppliedInCents,
    discountInCents,
    invoiceNumber,
    lineItemCount,
    state,
    subTotalInCents,
    taxInCents,
    totalInCents,
    countryCode,
  } = invoice;
  // we want to hide credit when the invoice failed, or when the user is being issued a credit/refund
  const hideCredit = (state && state.toUpperCase() === FAILED_INVOICE_STATE) || totalInCents < 0;
  const { calculatedCreditApplied, calculatedTotal } = getCalculatedValues(
    totalInCents,
    creditAppliedInCents,
    hideCredit,
  );

  const [showDetails, setShowDetails] = useState(false);
  const handleInvoiceViewed = (lineItems) => {
    track({
      trackName: 'Invoice Viewed',
      properties: {
        total_products: lineItemCount,
        billing_date: new Date(createdAt),
        event_position_order: tabIndex + 1,
        invoice_total: calculatedTotal,
        total_units: getTotalUnits(lineItems),
        invoice_status: state,
      },
    });
  };
  const toggleDetails = () => {
    if (!showDetails) {
      if (!invoiceData) {
        getInvoiceData(uuid).then((data) => {
          setInvoiceData(data);
          handleInvoiceViewed(data.lineItems);
        });
      }
      if (invoiceData && invoiceData.lineItems) {
        handleInvoiceViewed(invoiceData.lineItems);
      }
    }
    setShowDetails((prevState) => !prevState);
  };

  return (
    <div className={styles.invoiceContainer}>
      <div
        role="button"
        className={styles.invoiceTitle}
        onKeyDown={toggleDetails}
        onClick={toggleDetails}
        tabIndex={tabIndex}
      >
        <h3>{`Invoice #${invoiceNumber}`}</h3>
        <i
          className={`${
            showDetails ? 'fal fa-angle-down' : 'fal fa-angle-right'
          } ${styles.invoiceCaret}`}
        />
      </div>
      <div className={styles.invoiceFields}>
        <div className={styles.invoiceField}>
          <div className={styles.invoiceFieldName}>Invoice Date</div>
          <div className={styles.invoiceValue}>{formatDate(createdAt)}</div>
        </div>
        <div className={styles.invoiceField}>
          <div className={styles.invoiceFieldName}>Items</div>
          <div className={styles.invoiceValue}>{lineItemCount}</div>
        </div>
        <div className={styles.invoiceField}>
          <div className={styles.invoiceFieldName}>Total</div>
          <div className={styles.invoiceValue}>{formatCurrency(calculatedTotal)}</div>
        </div>
        <div className={styles.invoiceField}>
          <div className={styles.invoiceFieldName}>Status</div>
          <div className={styles.invoiceValue}>{capitalize(state)}</div>
        </div>
      </div>
      {invoiceData && showDetails && (
        <InvoiceDetails
          creditAppliedInCents={calculatedCreditApplied}
          discountInCents={discountInCents}
          invoiceNumber={invoiceNumber}
          lineItems={invoiceData.lineItems}
          subTotalInCents={subTotalInCents}
          taxInCents={taxInCents}
          calculatedTotal={calculatedTotal}
          countryCode={countryCode}
        />
      )}
    </div>
  );
};

Invoice.propTypes = {
  tabIndex: number.isRequired,
  invoice: shape({
    createdAt: string,
    creditAppliedInCents: number,
    invoiceNumber: string,
    lineItems: arrayOf(shape({})),
    state: string,
    subtotalInCents: number,
    taxInCents: number,
    totalInCents: number,
    countryCode: string,
  }).isRequired,
};

export default Invoice;
